/**
 * External dependencies
 */
import { ColumnConfig } from '@art-of-hospitality-solutions-llc/ui-kit/components/data-table';
import { Badge } from '@art-of-hospitality-solutions-llc/ui-kit/components/badge';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/card';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';

/**
 * Internal dependencies
 */
import { Card as InternalCard, CardContent as InternalCardContent } from '@Components/Card';
import { ResourceTableView } from '@Components/ResourceTable';
import { useResourceTableController } from '@Components/ResourceTable/useResourceTableController';
import Breadcrumbs from '@Components/Breadcrumbs';
import Heading from '@Components/Heading';
import Loader from '@Components/Loader';
import { useJobRuns, useJobRunsByType } from '@Hooks/useJobRuns';
import type { JobRun, JobStatus } from '@Types/jobRun';
import { buildFilterParams } from '@Utils/utils';
import { formatRunDuration } from '../formatRunDuration';

const STATUS_VARIANTS: Record<JobStatus, 'default' | 'destructive' | 'secondary'> = {
  SUCCESS: 'default',
  FAILURE: 'destructive',
  PENDING: 'secondary',
};

const CHART_COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1',
];

const runColumnConfigs: ColumnConfig<JobRun>[] = [
  {
    id: 'status',
    headerLabel: 'Status',
    accessor: (row) => row.status,
    type: 'text',
    cell: (info) => {
      const status = info.getValue() as JobStatus;
      return <Badge variant={STATUS_VARIANTS[status]}>{status}</Badge>;
    },
  },
  {
    id: 'createdAt',
    headerLabel: 'Created',
    accessor: (row) => row.createdAt ?? '',
    type: 'text',
    cell: (info) => {
      const date = info.getValue();
      if (!date) return 'N/A';
      return new Date(date as string).toLocaleString();
    },
  },
  {
    id: 'duration',
    headerLabel: 'Duration',
    accessor: (row) => formatRunDuration(row.results?.durationSeconds),
    type: 'text',
    enableSorting: false,
    enableFiltering: false,
  },
];

export default function JobRunTypeStats() {
  const { typeName } = useParams<{ typeName: string }>();
  const decodedType = typeName ? decodeURIComponent(typeName) : '';
  const navigate = useNavigate();

  // Chart data
  const { data: chartResponse, status: chartStatus } = useJobRunsByType(decodedType);

  const { chartData, numericKeys } = useMemo(() => {
    const runs = chartResponse?.data;
    if (!runs?.length) return { chartData: [], numericKeys: [] };

    const keySet = new Set<string>();
    for (const run of runs) {
      if (run.results && typeof run.results === 'object') {
        for (const [k, v] of Object.entries(run.results)) {
          if (typeof v === 'number') keySet.add(k);
        }
      }
    }

    const keys = Array.from(keySet).sort();
    const data = runs.map((run) => {
      const point: Record<string, any> = {
        createdAt: new Date(run.createdAt).toLocaleDateString(),
      };
      for (const k of keys) {
        point[k] = run.results?.[k] ?? null;
      }
      return point;
    });

    return { chartData: data, numericKeys: keys };
  }, [chartResponse]);

  // Runs table
  const controller = useResourceTableController<JobRun>({
    columnsConfig: runColumnConfigs,
    defaultSort: { id: 'createdAt', desc: true },
    buildFilterParams: (columnFilters) => buildFilterParams(columnFilters),
    initialPageSize: 10,
    baseSearchParams: () => ({
      'filter[type]': decodedType,
    }),
  });

  const { data: payload, status: tableStatus } = useJobRuns({
    searchParams: controller.searchParams,
  });

  const rows = useMemo(() => {
    if (!payload) return [];
    return Array.isArray(payload) ? payload : (payload?.data ?? []);
  }, [payload]);

  const meta = useMemo(() => {
    if (!payload || Array.isArray(payload)) return undefined;
    return payload.meta as { total?: number } | undefined;
  }, [payload]);

  return (
    <div className="w-full">
      <Breadcrumbs
        navList={[
          { name: 'Admin', href: '/job-runs' },
          { name: 'Job Runs', href: '/job-runs' },
          { name: decodedType, href: `/job-runs/type/${typeName}` },
        ]}
      />
      <div className="mt-8 mb-4">
        <Heading>{decodedType}</Heading>
        <p className="text-sm text-zinc-500">Stats and run history.</p>
      </div>

      {/* Chart */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Results Over Time</CardTitle>
        </CardHeader>
        <CardContent>
          {chartStatus === 'pending' ? (
            <Loader size="lg" />
          ) : !numericKeys.length ? (
            <div className="py-8 text-center text-sm text-zinc-500">
              No numeric data available for charting.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                <XAxis
                  dataKey="createdAt"
                  tick={{ fontSize: 12 }}
                  stroke="#71717a"
                />
                <YAxis tick={{ fontSize: 12 }} stroke="#71717a" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#18181b',
                    border: '1px solid #3f3f46',
                    borderRadius: 8,
                    fontSize: 13,
                  }}
                />
                <Legend />
                {numericKeys.map((key, i) => (
                  <Line
                    key={key}
                    type="monotone"
                    dataKey={key}
                    stroke={CHART_COLORS[i % CHART_COLORS.length]}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Runs table */}
      <InternalCard>
        <InternalCardContent>
          <ResourceTableView<JobRun>
            label="Runs"
            controller={controller}
            data={rows}
            meta={meta}
            status={tableStatus}
            onRowClick={(row) => navigate(`/job-runs/${row.id}`)}
          />
        </InternalCardContent>
      </InternalCard>
    </div>
  );
}
